import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Auth0Client } from "@auth0/auth0-spa-js";
import config from "../../config";
import { initialUserContext, jobSearchHistoryStub, userAcknowledgementsStub } from "./shared";
import { AccessToken, UserContextValue } from "./types";
import useTokenUserData from "./useTokenUserData";
import { isPublicPath } from "../../screens/NavigatorScreen/utils";
import { sendRaygunError } from "../useRaygun";
import useImpersonation from "./useImpersonation";

export const UserContext = createContext<UserContextValue>(initialUserContext);

function hasRedirectParams() {
  const searchCodes = ["state", "code", "error"];
  return searchCodes.some((code) => window.location.search.includes(code));
}

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [initialized, setInitialized] = useState(false);
  const [_accessToken, setAccessToken] = useState<AccessToken | null>(null);
  const { canImpersonate, impersonatedUser, assumeUser, clearAssumedUser } = useImpersonation(_accessToken);
  const currentUser = useTokenUserData(_accessToken);

  let auth0Client = new Auth0Client({
    domain: config.auth0Domain,
    clientId: config.auth0ClientId,
    authorizationParams: {
      audience: config.auth0Audience,
      redirect_uri: `${window.location.origin}/member`,
    },
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
  });

  const login = useCallback(async () => {
    await auth0Client!.loginWithRedirect({
      authorizationParams: {
        scope: `openid profile email offline_access`,
      },
      appState: { returnTo: window.location.href },
    });
  }, []);

  const logout = useCallback(async () => {
    await auth0Client!.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/logged-out`,
      },
    });
  }, []);

  const fetchAccessToken = useCallback(async (): Promise<AccessToken | null> => {
    try {
      const accessToken = (await auth0Client!.getTokenSilently()) as AccessToken;
      setAccessToken(accessToken);
      return accessToken;
    } catch (error: any) {
      const skippedErrors = ["login_required", "missing_refresh_token", "invalid_grant"];
      if (!skippedErrors.includes(error.error)) {
        throw error;
      }
      setAccessToken(null);
      return null;
    }
  }, []);

  const _handleRedirectCallback = async () => {
    try {
      const { appState } = await auth0Client!.handleRedirectCallback();
      if (appState?.returnTo) {
        window.location = appState.returnTo;
      }
    } catch (error) {
      console.error("Error handling redirect callback:", error);
      sendRaygunError(new Error(`Error handling redirect callback: ${error}`));
    }
  };

  useEffect(() => {
    if (initialized) return;

    const initialize = () => {
      if (isPublicPath()) {
        setInitialized(true);
        return;
      }

      fetchAccessToken().then((newToken) => {
        if (!newToken) {
          login();
        } else {
          setInitialized(true);
        }
      });
    };

    if (hasRedirectParams()) {
      _handleRedirectCallback();
    }

    initialize();
  }, [initialized]);

  if (!initialized) return null;

  const value: UserContextValue = {
    initialized,
    currentUser:
      currentUser != null
        ? {
            ...currentUser,
            ...(impersonatedUser ?? {}),
          }
        : null,
    getAccessToken: fetchAccessToken,
    login,
    logout,
    impersonatedUser,
    canImpersonate,
    assumeUser,
    clearAssumedUser,
    // these features aren't used on web
    ...userAcknowledgementsStub,
    ...jobSearchHistoryStub,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const useCurrentUser = () => useContext(UserContext);

export default useCurrentUser;
